import { calculateFutureTimestamp } from '@/utils/calculateFutureTimestamp'
import { StorageSerializers, useStorage } from '@vueuse/core'
import type {
	AddressResponse, Zone
} from '@/components/account/types'
import type { ServiceTier } from '@/composables/servicePackages'
import { mapLabelToStatus, mapZoneToStatus } from '@/utils/fiberStatusMap'

export function useLanguageLocalStorage() {
	const setLanguage = (language: string) => {
		localStorage.setItem('language', language)
	}
	const cachedLanguage = useStorage('language', null)

	return {
		setLanguage,
		cachedLanguage
	}
}

export function useContactLocalStorage() {
	const setPhoneNumberToLocalStorage = (phoneNumber: string) => {
		const expirationTime = calculateFutureTimestamp({ seconds: 10 })
		localStorage.setItem('phoneNumber', JSON.stringify({
			phoneNumber,
			expirationTime
		}))
	}

	const cachedPhoneNumberObject = useStorage('phoneNumber', null, undefined, { serializer: StorageSerializers.object })

	const getPhoneNumberFromLocalStorage = () => {
		return JSON.parse(localStorage?.getItem('phoneNumber') as string)
	}

	const set2faContactToLocalStorage = (contact: string) => {
		const expirationTime = calculateFutureTimestamp({ seconds: 10 })
		localStorage.setItem('2faContact', JSON.stringify({
			contact,
			expirationTime
		}))
		// #TODO make an abstracted function for all of these, add the removeitem for each
		setTimeout(() => {
			localStorage.setItem('2faContact', JSON.stringify({
				contact: '',
				expirationTime
			}))
		}, 10000)
	}

	const cached2faContactObject = useStorage('2faContact', null, undefined, { serializer: StorageSerializers.object })

	const get2faContactFromLocalStorage = () => {
		return JSON.parse(localStorage?.getItem('2faContact') as string)
	}

	const setEmailToLocalStorage = (email: string) => {
		const expirationTime = calculateFutureTimestamp({ seconds: 10 })
		localStorage.setItem('email', JSON.stringify({
			email,
			expirationTime
		}))
	}

	const cachedEmailObject = useStorage('email', null, undefined, { serializer: StorageSerializers.object })

	const getEmailFromLocalStorage = () => {
		return JSON.parse(localStorage?.getItem('email') as string)
	}

	return {
		setPhoneNumberToLocalStorage,
		cachedPhoneNumberObject,
		getPhoneNumberFromLocalStorage,
		set2faContactToLocalStorage,
		cached2faContactObject,
		get2faContactFromLocalStorage,
		setEmailToLocalStorage,
		cachedEmailObject,
		getEmailFromLocalStorage
	}
}

export function useZoneLocalStorage() {
	const setZoneToLocalStorage = (zoneId: string) => {
		localStorage.setItem('zoneId', zoneId)
	}
	const cachedZoneId = useStorage('zoneId', '')

	return {
		setZoneToLocalStorage,
		cachedZoneId
	}
}

export function useNavCollapseLocalStorage() {
	// Initialize with false for SSR consistency
	const isCollapsed = ref(false)
	const navCollapsed = useStorage('navCollapse', 'false')

	// Only set the initial value on client-side after hydration
	onMounted(() => {
		const storedValue = localStorage.getItem('navCollapse')
		if (storedValue !== null) {
			isCollapsed.value = storedValue === 'true'
			navCollapsed.value = storedValue
		}
	})

	// Keep isCollapsed in sync with navCollapsed changes
	watch(navCollapsed, newVal => {
		isCollapsed.value = newVal === 'true'
	})

	const toggleCollapsed = () => {
		isCollapsed.value = !isCollapsed.value
		navCollapsed.value = isCollapsed.value ? 'true' : 'false'
	}

	return {
		toggleCollapsed,
		navCollapsed,
		isCollapsed
	}
}

export function useAddressLocalStorage() {
	const defaultValue: AddressResponse = {
		address: '',
		addressId: 0,
		status: 'internetUnavailable', // Default status
		billables: [],
		accountFound: false
	}

	// Create reactive localStorage with object serialization
	const cachedAddressResponse = useStorage('addressResponse', defaultValue, undefined, { serializer: StorageSerializers.object })

	const setAddressObject = (address: string, addressId: number, zone: Zone, billables: ServiceTier[], accountFound: boolean, planPhaseLabel?: string) => {
		const status = planPhaseLabel ? mapLabelToStatus(planPhaseLabel, !!billables?.length) : mapZoneToStatus(zone)

		// Create clean data (no circular references)
		// Use structuredClone if available, otherwise fall back to JSON method
		let cleanBillables: ServiceTier[]
		try {
			cleanBillables = structuredClone ? structuredClone(billables) : JSON.parse(JSON.stringify(billables))
		} catch {
			// Fallback if structuredClone fails or billables has circular refs
			cleanBillables = JSON.parse(JSON.stringify(billables))
		}

		const addressData = {
			address,
			addressId,
			status,
			billables: cleanBillables,
			accountFound
		}

		// Update the reactive storage instead of manual localStorage
		cachedAddressResponse.value = addressData

		return addressData
	}
	return {
		cachedAddressResponse,
		setAddressObject
	}
}

export function use811ModalLocalStorage() {
	const MODAL_KEY = '811ModalLastShown'
	const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds

	const shouldShowModal = (): boolean => {
		if (typeof window === 'undefined') return false
		
		const lastShown = localStorage.getItem(MODAL_KEY)
		if (!lastShown) return true

		const lastShownDate = new Date(lastShown).getTime()
		const now = new Date().getTime()
		
		return (now - lastShownDate) >= ONE_MONTH_MS
	}

	const markModalAsShown = () => {
		if (typeof window === 'undefined') return
		
		const now = new Date().toISOString()
		localStorage.setItem(MODAL_KEY, now)
	}

	return {
		shouldShowModal,
		markModalAsShown
	}
}
